import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Topbar from '../components/Topbar';
import Breadcrumb from '../components/Breadcrumb';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import '../styles/FacturacionGeneral.css';

const FacturacionGeneral = () => {
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    nif: '',
    tratamiento: '',
    n_pieza: '',
    cuadrante: '',
    arcada: '',
    cantidad: '',
    importe: '',
    n_factura: '',
    clinica: '',
    presupuesto: ''
  });

  const [resultados, setResultados] = useState([]);
  const [mostrarGrafico, setMostrarGrafico] = useState(false);
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const calcularTotalFila = (r) => {
    const cantidad = Number(r.cantidad || 1);
    const importe = Number(r.importe || 0);
    const pendiente = Number(r.pendiente_pago || 0);
    const descuento = Number(r.descuento || 0);

    const bruto = cantidad * importe;
    const neto = Math.max(bruto - descuento, 0);
    return Math.max(neto - pendiente, 0);
  };

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const totalGlobal = resultados.reduce((acc, r) => {
    return acc + calcularTotalFila(r);
  }, 0);

  useEffect(() => {
    const hoy = new Date().toISOString().slice(0, 10);
    setFiltros(f => ({
      ...f,
      fechaInicio: hoy,
      fechaFin: hoy
    }));
  }, []);
  
  const handleBuscar = async () => {
    setLoading(true);
    try {
      let params = Object.fromEntries(
        Object.entries(filtros).filter(([_, v]) => v !== '' && v != null)
      );


      const res = await axios.get('/api/facturacion/general', {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setResultados(res.data);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    const datosExport = resultados.map(r => {
      const cantidad = Number(r.cantidad || 1);
      const importe = Number(r.importe || 0);
      const descuento = Number(r.descuento || 0);
      const pendiente = Number(r.pendiente_pago || 0);
      const total = calcularTotalFila(r);

      return {
        Día: r.dia ? new Date(r.dia).toLocaleDateString("es-ES") : "",
        Nombre: r.nombre,
        "Apellido 1": r.primer_apellido,
        "Apellido 2": r.segundo_apellido,
        NIF: r.nif,
        Tratamiento: r.tratamiento,
        Pieza: r.n_pieza,
        Cuadrante: r.cuadrante,
        Arcada: r.arcada,
        Cantidad: cantidad,
        Importe: `${importe.toFixed(2)} €`,
        Descuento: `${descuento.toFixed(2)} €`,
        Pendiente: `${pendiente.toFixed(2)} €`,
        Total: `${total.toFixed(2)} €`,
        "Nº Factura": r.n_factura,
        Clínica: r.clinica,
        Presupuesto: r.presupuesto
      };
    });

    const hoja = XLSX.utils.json_to_sheet(datosExport);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Facturación');

    const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    saveAs(blob, 'facturacion.xlsx');
  };

  const datosGrafico = {
    labels: resultados.map(r => r.dia),
    datasets: [{
      label: 'Importe total por fechas',
      data: resultados.map(r => calcularTotalFila(r)),
      backgroundColor: 'rgba(75, 192, 192, 0.6)'
    }]
  };

  return (
    <>
        <Topbar />
        <div className="facturacion-general">
        <Breadcrumb />
        <h2>📋 Datos de Facturación</h2>

        {/* Filtros */}
        <div className="filtros">
          <div className="filtro-fechas">
            <label>
              Fecha desde
              <input type="date" name="fechaInicio" value={filtros.fechaInicio} onChange={handleChange} />
            </label>
            <label>
              Fecha hasta
              <input type="date" name="fechaFin" value={filtros.fechaFin} onChange={handleChange} />
            </label>
          </div>
          <div className="filtro-campos">
            {Object.keys(filtros)
              .filter(campo => campo !== 'fechaInicio' && campo !== 'fechaFin')
              .map((campo) => {
                if (campo === "clinica") {
                  return (
                    <select key="clinica" name="clinica" value={filtros.clinica} onChange={handleChange}>
                      <option value="">-- Clínica --</option>
                      <option value="LA ELIPA">LA ELIPA</option>
                      <option value="VEN">VEN</option>
                    </select>
                  );
                }
                return (
                  <input
                    key={campo}
                    name={campo}
                    placeholder={campo.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    value={filtros[campo]}
                    onChange={handleChange}
                  />
                );
              })}
          </div>
          <button className="btn-buscar" onClick={handleBuscar}>🔍 Buscar</button>
        </div>

        {/* Botones intermedios */}
        <div className="acciones">
            <button onClick={exportarExcel}>📤 Exportar a Excel</button>
            <button onClick={() => setMostrarGrafico(!mostrarGrafico)}>📊 Ver Gráfico</button>
        </div>
        {/* Spinner */}
        {loading && (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <div className="spinner"></div>
            <p>Cargando datos...</p>
          </div>
        )}
        {/* Tabla */}
        {!loading && (
          <div className="tabla-wrapper">
            <table className="tabla-resultados">
                <thead>
                <tr>
                    <th>Día</th><th>Nombre</th><th>Apellido 1</th><th>Apellido 2</th><th>NIF</th>
                    <th>Tratamiento</th><th>Pieza</th><th>Cuadrante</th>
                    <th>Cantidad</th><th>Importe</th><th>Pendiente</th><th>Descuento</th><th>Total</th><th>Nº Factura</th><th>Clínica</th>
                </tr>
                </thead>
                <tbody>
                {resultados.map((r, i) => {
                    const cantidad = Number(r.cantidad || 1);
                    const importe = Number(r.importe || 0);
                    const total = calcularTotalFila(r);
                    
                    return(
                        <tr
                          key={i}
                          onClick={() => setFilaSeleccionada(i)}
                          className={filaSeleccionada === i ? "fila-seleccionada" : ""}
                        >
                          <td>{r.dia ? new Date(r.dia).toLocaleDateString("es-ES") : ""}</td>
                          <td>{r.nombre}</td>
                          <td>{r.primer_apellido}</td>
                          <td>{r.segundo_apellido}</td>
                          <td>{r.nif}</td>
                          <td>{r.tratamiento}</td>
                          <td>{r.n_pieza}</td>
                          <td>{r.cuadrante}</td>
                          <td>{cantidad}</td>
                          <td>{importe.toFixed(2)} €</td>
                          <td>{Number(r.pendiente_pago || 0).toFixed(2)} €</td>
                          <td>{Number(r.descuento || 0).toFixed(2)} €</td>
                          <td>{total.toFixed(2)} €</td>
                          <td>{r.n_factura}</td>
                          <td>{r.clinica}</td>
                          <td>{r.presupuesto}</td>
                      </tr>
                    );
                })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={12} style={{ textAlign: "right", fontWeight: "bold" }}>Total Global:</td>
                        <td style={{ fontWeight: "bold" }}>{totalGlobal.toFixed(2)} €</td>
                        <td colSpan={2}></td>
                    </tr>
                </tfoot>
            </table>
          </div>
        )}
        {/* Gráfico */}
        {mostrarGrafico && (
            <div className="grafico">
            <Bar data={datosGrafico} />
            </div>
        )}
        </div>
    </>        
  );
};

export default FacturacionGeneral;
